import { useIntl } from "react-intl";
import { DegreeRequirementSelect } from "@/components/design/DegreeRequirementSelect";
import { PlusButton } from "@/components/design/PlusButton";
import {
  newCondition,
  type AiMode,
  type ConditionByTypename,
  type MatchCondition,
  type MatchConditionTypename,
} from "../design-codec";
import type { MatchData } from "../match-design-types";
import { useRequirementsReferenceData } from "../use-requirements-reference-data";
import {
  RequirementControls,
  RowShell,
  SubsectionHeader,
  TextValueInput,
} from "./row-primitives";

type EducationCondition = ConditionByTypename<"EducationCondition">;
type CertificateCondition = ConditionByTypename<"CertificateCondition">;
type QualificationCondition = EducationCondition | CertificateCondition;
type QualificationTypename = QualificationCondition["__typename"];

const QUALIFICATION_TYPES: ReadonlySet<MatchConditionTypename> = new Set([
  "EducationCondition",
  "CertificateCondition",
]);

const CERTIFICATE_PLACEHOLDER = {
  id: "match.recruiting.certificatePlaceholder",
  defaultMessage: "z. B. AWS Solutions Architect",
};

// Ausbildung + Zertifikate teilen sich eine Untersektion (beide sind formale Nachweise),
// analog zu "Fachkompetenzen & Berufserfahrung" im Team-Modus (spark #230).
export function RecruitingQualifications({
  data,
  update,
  readOnly = false,
  className = "",
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
  className?: string;
}) {
  const intl = useIntl();
  const { degrees } = useRequirementsReferenceData();

  const rows = data.conditions.filter(
    (condition): condition is QualificationCondition => QUALIFICATION_TYPES.has(condition.__typename),
  );
  const aiOn = rows.some((condition) => condition.aiMode === "FULL_AI");

  function writeRows(next: QualificationCondition[]) {
    const others = data.conditions.filter((condition) => !QUALIFICATION_TYPES.has(condition.__typename));
    update({ conditions: [...others, ...next] as MatchCondition[] });
  }

  function patch(index: number, values: Partial<QualificationCondition>) {
    const current = rows[index];
    if (!current) return;
    const next = rows.slice();
    next[index] = { ...current, ...values } as QualificationCondition;
    writeRows(next);
  }

  function remove(index: number) {
    const next = rows.slice();
    next.splice(index, 1);
    writeRows(next);
  }

  function add(typename: QualificationTypename) {
    const aiMode: AiMode = aiOn ? "FULL_AI" : "NO_AI";
    const shared = {
      value: "",
      aiMode,
      operator: "EQ" as const,
      composition: { operator: "EXACT" as const, value: 1 },
    };
    const condition: QualificationCondition = typename === "EducationCondition"
      ? newCondition("EducationCondition", shared)
      : newCondition("CertificateCondition", shared);
    writeRows([...rows, condition]);
  }

  // Untertitel immer sichtbar (auch ohne Zeile) — nicht erst hinter dem Add-Button (spark #230).
  if (readOnly && rows.length === 0) return null;

  // Kennungen je Typ gezählt, aus der ORIGINALREIHENFOLGE — die Anzeige unten
  // sortiert Muss nach oben und würde sie beim Umlegen eines Schlosses tauschen.
  // Zwei Präfixe: `requirement-row-…` für die Zeile, `requirement-…` für ihre
  // Bedienelemente, sonst trifft ein Präfixwähler auf Zeilen die Kinder mit.
  const rowIds = new Map<number, string>();
  const fieldIds = new Map<number, string>();
  {
    const gezaehlt = new Map<string, number>();
    rows.forEach((c, i) => {
      const typ = c.__typename.toLowerCase();
      const n = gezaehlt.get(c.__typename) ?? 0;
      gezaehlt.set(c.__typename, n + 1);
      rowIds.set(i, `requirement-row-${typ}-${n}`);
      fieldIds.set(i, `requirement-${typ}-${n}`);
    });
  }

  const displayRows = rows.map((condition, index) => ({ condition, index }));
  displayRows.sort(
    (a, b) => Number(b.condition.requirement === "MUST") - Number(a.condition.requirement === "MUST"),
  );

  return (
    <div className={className}>
      <SubsectionHeader
        title={intl.formatMessage({
          id: "match.recruiting.qualificationsTitle",
          defaultMessage: "Ausbildung & Zertifikate",
        })}
      />
      <div className="space-y-2">
        {displayRows.map(({ condition, index }) => (
          <RowShell
            key={`${condition.__typename}-${index}`}
            id={rowIds.get(index)}
            removeId={fieldIds.get(index) ? `${fieldIds.get(index)}-remove` : undefined}
            onRemove={() => remove(index)}
            readOnly={readOnly}
          >
            <RequirementControls
              id={fieldIds.get(index) ? `${fieldIds.get(index)}-lock` : undefined}
              requirement={condition.requirement}
              onChange={(requirement) => patch(index, { requirement })}
              readOnly={readOnly}
            />
            {condition.__typename === "EducationCondition" ? (
              <DegreeRequirementSelect
                id={fieldIds.get(index) ? `${fieldIds.get(index)}-value` : undefined}
                value={condition.value}
                onChange={(value) => patch(index, { value, operator: "EQ" })}
                options={degrees.map((degree) => ({
                  value: degree.label,
                  label: degree.label,
                }))}
                readOnly={readOnly}
              />
            ) : (
              <TextValueInput
                id={fieldIds.get(index) ? `${fieldIds.get(index)}-value` : undefined}
                value={condition.value}
                onChange={(value) => patch(index, { value })}
                readOnly={readOnly}
                placeholder={intl.formatMessage(CERTIFICATE_PLACEHOLDER)}
              />
            )}
          </RowShell>
        ))}
        {!readOnly ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
            <PlusButton
              id="requirement-add-educationcondition"
              label={intl.formatMessage({ id: "match.recruiting.addEducation", defaultMessage: "Ausbildung" })}
              onClick={() => add("EducationCondition")}
            />
            <PlusButton
              id="requirement-add-certificatecondition"
              label={intl.formatMessage({ id: "match.recruiting.addCertificate", defaultMessage: "Zertifikat" })}
              onClick={() => add("CertificateCondition")}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
